<template>
  <v-form @submit.prevent="submitDevice" ref="deviceForm">
    <v-card rounded="lg">
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="text-h5 font-weight-black ps-2">
          {{ $t('i18n.createDeviceModalTitle') }}
        </div>
        <v-btn icon="mdi-close" variant="text" @click="closeModal" size="x-large"></v-btn>
      </v-card-title>
      <v-card-text class="overflow-y-auto">
        <div class="font-weight-black">{{ $t('i18n.name') }}</div>
        <v-text-field density="compact" :placeholder="$t('i18n.namePlaceholder')" v-model="newDeviceObject.name" variant="outlined" :rules="deviceNameRules" clearable></v-text-field>
        <!-- UUID -->
        <div class="font-weight-black">{{ $t('i18n.uuidLabel') }}</div>
        <v-text-field density="compact" v-model="newDeviceObject.uuid" variant="outlined" :rules="deviceUuidRules" hide-details="auto" clearable></v-text-field>
        <div class="mt-2 mb-6">{{ $t('i18n.uuidHint') }}</div>
        <!-- End UUID -->
        <div class="font-weight-black">{{ $t('i18n.description') }}</div>
        <v-text-field density="compact" :placeholder="$t('i18n.descriptionPlaceholder')" v-model="newDeviceObject.description" :rules="deviceDescriptionRules" variant="outlined" clearable>
        </v-text-field>
        <div class="font-weight-black">{{ $t('i18n.location') }}</div>
        <v-text-field density="compact" :placeholder="$t('i18n.locationPlaceholder')" v-model="newDeviceObject.location" :rules="deviceLocationRules" variant="outlined" clearable></v-text-field>
        <div class="font-weight-black">{{ $t('i18n.deviceTypeLabel') }}</div>
        <v-select v-model="newDeviceObject.type_id" :items="deviceTypeList" item-value="id" item-title="name" :placeholder="$t('i18n.deviceTypePlaceholder')" variant="outlined" :rules="deviceTypeRules" density="compact" required :no-data-text="$t('i18n.noDeviceTypes')"></v-select>
        <div v-if="newDeviceObject.type_id === 1"> <!-- TABLET -->
          <div class="font-weight-black">{{ $t('i18n.ipAddress') }}</div>
          <v-text-field v-model="newDeviceObject.ip_address" density="compact" placeholder="192.168.0.90" variant="outlined" :rules="deviceIpRules" clearable>
          </v-text-field>
        </div>
        <div v-if="newDeviceObject.type_id === 2"> <!-- QR CODE -->
          <div class="font-weight-black">{{ $t('i18n.completionRedirectUrl') }}</div>
          <v-text-field density="compact" hide-details="auto" v-model="newDeviceObject.completion_redirect_url" placeholder="https://my-company.com" variant="outlined" :rules="urlRules" clearable></v-text-field>
          <div class="mt-2 mb-6 text-start">{{ $t('i18n.completionRedirectUrlHint') }}</div>
        </div>
        <div class="font-weight-black">{{ $t('i18n.associateFormLabel') }}</div>
        <v-select v-model="newDeviceObject.form_id" :items="simplifiedFormList" :no-data-text="$t('i18n.noForms')" item-value="id" item-title="name" density="compact" chips closable-chips :placeholder="$t('i18n.associateFormPlaceholder')" variant="outlined"></v-select>
      </v-card-text>
      <v-card-actions>
        <v-btn class="gorate-btn-bg-outlined" rounded="xl" variant="outlined" :text="$t('i18n.closeBtnText')" @click="closeModal"></v-btn>
        <v-btn class="gorate-btn-bg" rounded="xl" variant="flat" :text="$t('i18n.saveBtnText')" type="submit" :disabled="ongoingRequest"></v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { Device, DeviceType, FormSchema } from '@/utils/DataTypes'
import { VForm } from 'vuetify/lib/components/index.mjs'
import { useRouter } from 'vue-router'
import { useAppStore } from '@/stores/appStore'
import { api, getRandomUid } from '@/utils/HelperFunctions'

export default defineComponent({
  name: 'CreateDeviceModal',
  setup () {
    const router = useRouter()
    const appStore = useAppStore()
    return {
      router,
      appStore
    }
  },
  async mounted () {
    this.deviceTypeList = this.appStore.getAppState.deviceTypeList
    await this.getAllForms()
  },
  props: {
    currentDeviceList: {
      type: Array as PropType<Device[]>,
      required: true
    }
  },
  data () {
    return {

      formMap: new Map<string, FormSchema>(),
      simplifiedFormList: [] as Array<{ id: number | undefined; name: string }>,

      newDeviceObject: {
        uuid: undefined,
        name: '',
        description: '',
        location: '',
        type_id: null,
        form_id: null,
        completion_redirect_url: null,
        ip_address: null
      } as Device,

      deviceTypeList: [] as Array<DeviceType>,

      ongoingRequest: false
    }
  },
  methods: {
    async submitDevice () {
      const newDeviceIsValid = await this.validateNewDevice()
      if (!newDeviceIsValid) return

      try {
        this.ongoingRequest = true
        const url = '/devices'
        const createDeviceResponse = await api(url, 'post', this.newDeviceObject)

        if (!createDeviceResponse) return

        // const createDeviceResponseData = createDeviceResponse.data

        if (createDeviceResponse.payload.statusCode === 201) this.appStore.addAlert('success', createDeviceResponse.payload.responseMessage)
        else this.appStore.addAlert('error', createDeviceResponse.payload.responseMessage)
      } catch (error) {
        this.appStore.addAlert('error', 'Error creating device. Please contact support.')
      } finally {
        this.closeModal()
      }
    },
    async getAllForms () {
      try {
        const url = '/forms'
        const getAllFormsResponse = await api(url, 'get')

        if (!getAllFormsResponse) return

        const formsObject = getAllFormsResponse.data as Record<string, any>
        this.formMap = new Map<string, FormSchema>(
          Object.entries(formsObject).map(([key, value]) => {
            const v = value as any
            return [
              key,
              {
                form: {
                  id: v.id,
                  name: v.name,
                  description: v.description,
                  layout_mode: v.layout_mode
                },
                form_items: v.form_items
              }
            ]
          })
        )
        this.simplifiedFormList = Array.from(this.formMap.values()).map((formSchema) => {
          return {
            id: formSchema.form.id,
            name: formSchema.form.name
          }
        })
      } catch (error) {
        console.error('Error getting forms list: ', error)
      }
    },
    async validateNewDevice () {
      const deviceForm = this.$refs.deviceForm as VForm
      try {
        const { valid } = await deviceForm.validate()
        return valid
      } catch (error) {
        console.error('Validation Error: ', error)
        this.appStore.addAlert('error', 'Validation error. Please check input fields again.')
      }
    },
    navigate (routeName: string) {
      this.router.push({ name: routeName })
    },
    closeModal () {
      this.$emit('close-modal')
    }
  },
  computed: {
    getDeviceTypeNameList (): string[] {
      return this.deviceTypeList.map(item => item.name)
    },
    deviceNameRules (): ((value: string | null | undefined) => boolean | string)[] {
      return [
        (value: string | null | undefined) => {
          if (value) return true
          return this.$t('i18n.deviceNameRequired')
        }, (value: string | null | undefined) => {
          if (value && value.length <= 30) return true
          return this.$t('i18n.fieldMaxLength30')
        }, (value: string | null | undefined) => {
          if (value && value.length >= 3) return true
          return this.$t('i18n.fieldMinLength3')
        }, (value: string | null | undefined) => {
          const normalizedValue = (value ?? '').trim()
          if (!normalizedValue) return true

          const deviceNameAlreadyExists = this.currentDeviceList.some(device => (device.name ?? '').trim() === normalizedValue)
          if (deviceNameAlreadyExists) {
            return this.$t('i18n.deviceNameExists')
          }
          return true
        }
      ]
    },
    deviceUuidRules (): ((value: string | null | undefined) => boolean | string)[] {
      return [
        (value: string | null | undefined) => {
          const normalizedValue = (value ?? '').trim()
          if (!normalizedValue) return true

          const uuidAlreadyExists = this.currentDeviceList.some((device) => {
            if (device.uuid === null || device.uuid === undefined) {
              return false
            } else {
              return device.uuid.trim() === normalizedValue
            }
          })
          if (uuidAlreadyExists) {
            return this.$t('i18n.deviceUuidExists')
          }
          return true
        }
      ]
    },
    deviceDescriptionRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value && value.length > 30) return this.$t('i18n.fieldMaxLength30')
          return true
        }
      ]
    },
    deviceLocationRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value && value.length > 30) return this.$t('i18n.fieldMaxLength30')
          return true
        }
      ]
    },
    deviceTypeRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (value) return true
          return this.$t('i18n.deviceTypeRequired')
        }
      ]
    },
    urlRules (): ((value: string) => boolean | string)[] {
      return [
        (value: string) => {
          if (!value) return true
          try {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const parsedUrl = new URL(value)
            return true
          } catch (e) {
            return this.$t('i18n.URLisInvalid')
          }
        }
      ]
    },
    deviceIpRules (): ((value: string | null | undefined) => boolean | string)[] {
      return [
        /* (value: string) => {
          if (value) return true
          return this.$t('i18n.deviceIpRequired')
        },  */
        (value: string | null | undefined) => {
          const normalizedValue = (value ?? '').trim()
          if (!normalizedValue) return true

          const ipAlreadyExists = this.currentDeviceList.some((device) => {
            if (device.ip_address === null || device.ip_address === undefined) {
              return false
            } else {
              return device.ip_address.trim() === normalizedValue
            }
          })
          if (ipAlreadyExists) {
            return this.$t('i18n.deviceIpExists')
          }
          return true
        }
      ]
    }
  }
})
</script>

<style lang="scss" scoped>
@import "../../../styles/colors.scss";

.button-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}
</style>
